import { LinxStatus } from '@shared/constants/app-constants';
import { WorkflowStepErrorResponse } from '@shared/models';

import { CUSTOM_FIELD_UNRESOLVED, WORKFLOW_STATUS_INVALID } from './custom-field-errors';
import { CURRENCY_ERROR_CODES } from './debit-authorization-errors';

/** What a failed API call leaves in Sentry (KOR-15194) */
export type ApiFailureReport = 'event' | 'breadcrumb' | 'none';

/**
 * The `errorCode`s a step acts on, so their failures are expected and not reported. A code the app starts handling
 * belongs here too, or every occurrence of it becomes a Sentry event.
 */
export const HANDLED_ERROR_CODES: ReadonlySet<string> = new Set([
  WORKFLOW_STATUS_INVALID,
  CUSTOM_FIELD_UNRESOLVED,
  ...CURRENCY_ERROR_CODES,
]);

/** Gateway, pod unavailable, timeout: the request never reached a service that could report it */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

const asBody = (body: unknown): WorkflowStepErrorResponse => (body && typeof body === 'object' ? (body as WorkflowStepErrorResponse) : {});

/**
 * A plain 500 is meant to be reported by the backend with its cause, leaving only a breadcrumb here. No KOR service
 * reports to Sentry yet (KOR-15193 adds it), so until that is live in every environment the browser reports it too.
 * Then set this to false - KOR-15194 decision D4.
 */
const REPORT_SERVER_ERRORS_FROM_BROWSER = true;

/**
 * Which failed API calls the browser has to report itself: the ones the backend never sees (network, gateway), the
 * 4xx answers the app has no handling for, and for now a plain 500 (see above). Planned maintenance has its own page.
 */
export interface ApiFailureOptions {
  /** Report a plain 500 as an event (default: `REPORT_SERVER_ERRORS_FROM_BROWSER`) */
  reportServerErrors?: boolean;
}

export const classifyApiFailure = (
  status: number,
  body: unknown,
  { reportServerErrors = REPORT_SERVER_ERRORS_FROM_BROWSER }: ApiFailureOptions = {},
): ApiFailureReport => {
  if (body === LinxStatus.SYSTEM_MAINTENANCE) return 'breadcrumb';
  if (status === 0 || GATEWAY_STATUSES.has(status)) return 'event';
  if (status === 500) return reportServerErrors ? 'event' : 'breadcrumb';
  if (status < 400 || status >= 500) return 'none';

  const { errorCode, errors } = asBody(body);
  if (errors?.length) return 'none';

  return errorCode && HANDLED_ERROR_CODES.has(errorCode) ? 'none' : 'event';
};
